import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Menu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableComponent from "../../Generals/Tables/TableComponent";
import TabsComponent from "../../Generals/TabPanels/TabsMUIComponent";
import SearchBoxComponent from "../../Generals/SearchBox/SearchBoxMUI";
import ButtonMain from "../../Generals/Buttons/ButtonMain";
import ExportIconButton from "../../Generals/Buttons/ExportIconButton";
import SelectorSimple from "../../Generals/Selectors/SelectorSimple";
import { slimSelectorStyles } from "../../Generals/Selectors/Styles";
import NotificationModal from "../../Generals/Notifications/NotificationModal";
import FormSystemModal from "../../Generals/Modals/FormSystemModal";
import jsPDF from "jspdf";
import "jspdf-autotable";

const TABS = [
  { label: "Signals", value: "Signal" },
  { label: "Followers", value: "Follower" },
];

const activityOptions = [
  { value: "", label: " " },
  { value: "EXIST_IN_MT", label: "Exist in MT" },
  { value: "ENABLE_IN_CT", label: "Enable in CT" },
];

// Columns for signals are constant to avoid recalculation
const SIGNAL_COLUMNS = [
  { field: "login", headerName: "Login", flex: 1 },
  { field: "name", headerName: "Name", flex: 1 },
  { field: "strategy_name", headerName: "Strategy Name", flex: 1 },
  { field: "fee_value_percentage", headerName: "Fee %", flex: 1 },
  { field: "total_followers", headerName: "Total Followers", flex: 1 },
  {
    field: "visibility",
    headerName: "Visibility",
    flex: 1,
    valueFormatter: ({ value }) => (value ? "Yes" : "No"),
  },
  { field: "drawdown", headerName: "Drawdown", flex: 1 },
  { field: "max_drawdown", headerName: "Max Drawdown", flex: 1 },
  {
    field: "available_in_mt",
    headerName: "Available In MT",
    flex: 1,
    valueFormatter: ({ value }) => (value ? "Yes" : "No"),
  },
  { field: "group", headerName: "Group", flex: 1 },
  { field: "currency", headerName: "Currency", flex: 1 },
  { field: "ib_account", headerName: "IB Account", flex: 1 },
  {
    field: "status_of_ib_account",
    headerName: "Status IB",
    flex: 1,
    valueFormatter: ({ value }) => (value ? "Yes" : "No"),
  },
];

// Columns for followers are also constant
const FOLLOWER_COLUMNS = [
  { field: "login", headerName: "Login", flex: 1 },
  { field: "name", headerName: "Name", flex: 1 },
  { field: "signal_login", headerName: "Signal Login", flex: 1 },
  { field: "copy_mode", headerName: "Copy Mode", flex: 1 },
  { field: "risk_ratio", headerName: "Risk Ratio", flex: 1 },
  { field: "min_lot_size", headerName: "Min Lot Size", flex: 1 },
  { field: "max_lot_size", headerName: "Max Lot Size", flex: 1 },
  { field: "drawdown_mode", headerName: "Drawdown Mode", flex: 1 },
  { field: "max_drawdown", headerName: "Max Drawdown", flex: 1 },
  { field: "following_date", headerName: "Following Date", flex: 1 },
  { field: "modification_date", headerName: "Modification Date", flex: 1 },
  {
    field: "visibility_follower",
    headerName: "Visibility Follower",
    flex: 1,
    valueFormatter: ({ value }) => (value ? "Yes" : "No"),
  },
  {
    field: "available_in_mt",
    headerName: "Available In MT",
    flex: 1,
    valueFormatter: ({ value }) => (value ? "Yes" : "No"),
  },
  { field: "group", headerName: "Group", flex: 1 },
  { field: "currency", headerName: "Currency", flex: 1 },
];

// CSV Export function
const exportCSV = (columns, rows, fileName) => {
  const header = columns.map((col) => col.headerName).join(",");
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const value =
            typeof col.valueFormatter === "function"
              ? col.valueFormatter({ value: row[col.field], row })
              : row[col.field];
          return `"${value ?? ""}"`;
        })
        .join(",")
    )
    .join("\n");
  const csvContent = `${header}\n${body}`;
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", `${fileName || "table"}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// TXT Export function
const exportTXT = (columns, rows, fileName) => {
  const header = columns.map((col) => col.headerName).join("\t");
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const value =
            typeof col.valueFormatter === "function"
              ? col.valueFormatter({ value: row[col.field], row })
              : row[col.field];
          return `${value ?? ""}`;
        })
        .join("\t")
    )
    .join("\n");
  const txtContent = `${header}\n${body}`;
  const blob = new Blob([txtContent], { type: "text/plain;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", `${fileName || "table"}.txt`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// PDF Export function
const exportPDF = (columns, rows, fileName) => {
  const doc = new jsPDF();
  const tableColumn = columns.map((col) => col.headerName);
  const tableRows = rows.map((row) =>
    columns.map((col) => {
      const value =
        typeof col.valueFormatter === "function"
          ? col.valueFormatter({ value: row[col.field], row })
          : row[col.field];
      return value ?? "";
    })
  );
  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    theme: "grid",
    styles: { fontSize: 10 },
  });
  doc.save(`${fileName || "table"}.pdf`);
};

const AccountsScreen = () => {
  // State declarations
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [role, setRole] = useState("Signal");
  const [activity, setActivity] = useState("");
  const [groups, setGroups] = useState("All");
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const exportOpen = Boolean(anchorEl);

  // Reusable modal configs for notification and form modal
  const [modalConfig, setModalConfig] = useState({
    open: false,
    iconType: "info",
    title: "",
    message: "",
    onAcceptLabel: "OK",
    onRefuseLabel: undefined,
    onContinueLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
    onContinue: undefined,
    onClose: () => setModalConfig((prev) => ({ ...prev, open: false })),
  });

  const [formModalConfig, setFormModalConfig] = useState({
    open: false,
    title: "",
    fields: [],
    onSubmit: null,
    onCancel: null,
    type: "form",
    confirmLabel: "Save",
    cancelLabel: "Cancel",
  });

  const [selectedAccount, setSelectedAccount] = useState(null);

  // Handlers for opening and closing modals
  const openNotification = (config) => setModalConfig({ ...modalConfig, ...config, open: true });
  const closeNotification = () => setModalConfig((prev) => ({ ...prev, open: false }));

  const openFormModal = (config) => setFormModalConfig({ ...formModalConfig, ...config, open: true });
  const closeFormModal = () => setFormModalConfig((prev) => ({ ...prev, open: false }));

  // Fetch accounts data
  const fetchAccounts = async ({ rol = role, filter = "", activity = "", groups = "All" } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/reports/accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rol, filter, activity, groups }),
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts((data?.data?.information_admin || []).map((acc, idx) => ({ ...acc, id: acc.login ?? idx })));
      } else {
        const err = await response.json();
        setError(err.message ?? "Server error");
        setAccounts([]);
        openNotification({
          iconType: "error",
          title: "Error",
          message: err.message || "Server error",
          onAcceptLabel: "OK",
          onClose: closeNotification,
        });
      }
    } catch (error) {
      setError("Failed to fetch accounts");
      setAccounts([]);
      openNotification({
        iconType: "error",
        title: "Network Error",
        message: "Failed to connect to server. Please check your connection.",
        onAcceptLabel: "OK",
        onClose: closeNotification,
      });
    }
    setLoading(false);
  };

  // Run fetch on role or groups change
  useEffect(() => {
    fetchAccounts({ rol: role, filter: "", activity: "", groups });
  }, [role, groups]);

  // Handle search form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    fetchAccounts({ rol: role, filter, activity, groups });
  };

  /** 
   * CRUD implementations for Signals and Followers
   * For creating signals and followers, editing signals and followers, deleting signals and followers
   * Each opens the FormSystemModal and handles server communication with notifications
   **/

  // -- Signals CRUD

  const handleOpenCreateSignalForm = () => {
    setSelectedAccount(null);
    openFormModal({
      title: "Create Signal",
      fields: [
        { name: "account", label: "Account", type: "text", value: "", required: true },
        { name: "password", label: "Password", type: "password", value: "", required: true },
        { name: "ip", label: "IP Address", type: "text", value: "", required: true },
      ],
      onSubmit: handleCreateSignal,
      onCancel: closeFormModal,
      type: "form",
    });
  };

  const handleCreateSignal = async (formData) => {
    closeFormModal();
    setLoading(true);
    try {
      const response = await fetch("/api/become-signal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        openNotification({
          iconType: "error",
          title: "Error creating signal",
          message: data.message || "Failed to create signal.",
          onAcceptLabel: "OK",
          onClose: closeNotification,
        });
      } else {
        openNotification({
          iconType: "success",
          title: "Success",
          message: data.message || "Signal created successfully.",
          onAcceptLabel: "OK",
          onClose: () => {
            closeNotification();
            fetchAccounts();
          },
        });
      }
    } catch (e) {
      openNotification({
        iconType: "error",
        title: "Network Error",
        message: "Error connecting to server. Please try again later.",
        onAcceptLabel: "OK",
        onClose: closeNotification,
      });
    }
    setLoading(false);
  };

  const handleOpenEditForm = (account) => {
    setSelectedAccount(account);
    openFormModal({
      title: "Edit Signal",
      fields: [
        { name: "name", label: "Name", type: "text", value: account.name || "", required: true },
        { name: "strategy_name", label: "Strategy Name", type: "text", value: account.strategy_name || "", required: true },
        { name: "fee_value_percentage", label: "Fee %", type: "number", value: account.fee_value_percentage || 0, required: true },
        { name: "max_drawdown", label: "Max Drawdown", type: "number", value: account.max_drawdown || 0, required: true },
        { name: "group", label: "Group", type: "text", value: account.group || "", required: false },
        { name: "currency", label: "Currency", type: "text", value: account.currency || "", required: true },
      ],
      onSubmit: handleEditSignal,
      onCancel: closeFormModal,
      type: "form",
    });
  };

  const handleEditSignal = async (formData) => {
    closeFormModal();
    setLoading(true);
    try {
      const accountLogin = selectedAccount?.login;
      if (!accountLogin) throw new Error("Account login missing");
      const response = await fetch(`/api/become-signal/${accountLogin}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        openNotification({
          iconType: "error",
          title: "Error editing signal",
          message: data.message || "Failed to edit signal.",
          onAcceptLabel: "OK",
          onClose: closeNotification,
        });
      } else {
        openNotification({
          iconType: "success",
          title: "Success",
          message: data.message || "Signal edited successfully.",
          onAcceptLabel: "OK",
          onClose: () => {
            closeNotification();
            fetchAccounts();
          },
        });
      }
    } catch (e) {
      openNotification({
        iconType: "error",
        title: "Network Error",
        message: "Error connecting to server. Please try again later.",
        onAcceptLabel: "OK",
        onClose: closeNotification,
      });
    }
    setLoading(false);
  };

  const handleDeleteSignal = async (account) => {
    if (!window.confirm("Are you sure you want to delete this signal?")) return;
    setLoading(true);
    try {
      const response = await fetch("/api/become-signal", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: account.login }),
      });
      const data = await response.json();
      if (!response.ok) {
        openNotification({
          iconType: "error",
          title: "Error deleting signal",
          message: data.message || "Failed to delete signal.",
          onAcceptLabel: "OK",
          onClose: closeNotification,
        });
      } else {
        openNotification({
          iconType: "success",
          title: "Success",
          message: data.message || "Signal deleted successfully.",
          onAcceptLabel: "OK",
          onClose: () => {
            closeNotification();
            fetchAccounts();
          },
        });
      }
    } catch (e) {
      openNotification({
        iconType: "error",
        title: "Network Error",
        message: "Error connecting to server. Please try again later.",
        onAcceptLabel: "OK",
        onClose: closeNotification,
      });
    }
    setLoading(false);
  };

  // -- Followers CRUD

  const handleOpenCreateFollowerForm = () => {
    setSelectedAccount(null);
    openFormModal({
      title: "Create Follower",
      fields: [
        { name: "signal_followed", label: "Signal Followed", type: "text", value: "", required: true },
        { name: "copy_mode", label: "Copy Mode", type: "text", value: "", required: true },
        { name: "drawdown_mode", label: "Drawdown Mode", type: "text", value: "", required: true },
        { name: "risk_ratio", label: "Risk Ratio", type: "number", value: 0, required: false },
        { name: "min_lot_size", label: "Min Lot Size", type: "number", value: 0, required: false },
        { name: "max_lot_size", label: "Max Lot Size", type: "number", value: 0, required: false },
      ],
      onSubmit: handleCreateFollower,
      onCancel: closeFormModal,
      type: "form",
    });
  };

  const handleCreateFollower = async (formData) => {
    closeFormModal();
    setLoading(true);
    try {
      const response = await fetch("/api/follow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        openNotification({
          iconType: "error",
          title: "Error creating follower",
          message: data.message || "Failed to create follower.",
          onAcceptLabel: "OK",
          onClose: closeNotification,
        });
      } else {
        openNotification({
          iconType: "success",
          title: "Success",
          message: data.message || "Follower created successfully.",
          onAcceptLabel: "OK",
          onClose: () => {
            closeNotification();
            fetchAccounts();
          },
        });
      }
    } catch (e) {
      openNotification({
        iconType: "error",
        title: "Network Error",
        message: "Error connecting to server. Please try again later.",
        onAcceptLabel: "OK",
        onClose: closeNotification,
      });
    }
    setLoading(false);
  };

  const handleOpenEditFollowerForm = (account) => {
    setSelectedAccount(account);
    openFormModal({
      title: "Edit Follower",
      fields: [
        { name: "signal_followed", label: "Signal Followed", type: "text", value: account.signal_login || "", required: true },
        { name: "copy_mode", label: "Copy Mode", type: "text", value: account.copy_mode || "", required: true },
        { name: "drawdown_mode", label: "Drawdown Mode", type: "text", value: account.drawdown_mode || "", required: true },
        { name: "risk_ratio", label: "Risk Ratio", type: "number", value: account.risk_ratio || 0, required: false },
        { name: "min_lot_size", label: "Min Lot Size", type: "number", value: account.min_lot_size || 0, required: false },
        { name: "max_lot_size", label: "Max Lot Size", type: "number", value: account.max_lot_size || 0, required: false },
      ],
      onSubmit: handleEditFollower,
      onCancel: closeFormModal,
      type: "form",
    });
  };

  const handleEditFollower = async (formData) => {
    closeFormModal();
    setLoading(true);
    try {
      const accountLogin = selectedAccount?.login;
      if (!accountLogin) throw new Error("Account login missing");
      const response = await fetch(`/api/follow/${accountLogin}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        openNotification({
          iconType: "error",
          title: "Error editing follower",
          message: data.message || "Failed to edit follower.",
          onAcceptLabel: "OK",
          onClose: closeNotification,
        });
      } else {
        openNotification({
          iconType: "success",
          title: "Success",
          message: data.message || "Follower edited successfully.",
          onAcceptLabel: "OK",
          onClose: () => {
            closeNotification();
            fetchAccounts();
          },
        });
      }
    } catch (e) {
      openNotification({
        iconType: "error",
        title: "Network Error",
        message: "Error connecting to server. Please try again later.",
        onAcceptLabel: "OK",
        onClose: closeNotification,
      });
    }
    setLoading(false);
  };

  const handleDeleteFollower = async (account) => {
    if (!window.confirm("Are you sure you want to delete this follower?")) return;
    setLoading(true);
    try {
      const response = await fetch("/api/follow", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: account.login }),
      });
      const data = await response.json();
      if (!response.ok) {
        openNotification({
          iconType: "error",
          title: "Error deleting follower",
          message: data.message || "Failed to delete follower.",
          onAcceptLabel: "OK",
          onClose: closeNotification,
        });
      } else {
        openNotification({
          iconType: "success",
          title: "Success",
          message: data.message || "Follower deleted successfully.",
          onAcceptLabel: "OK",
          onClose: () => {
            closeNotification();
            fetchAccounts();
          },
        });
      }
    } catch (e) {
      openNotification({
        iconType: "error",
        title: "Network Error",
        message: "Error connecting to server. Please try again later.",
        onAcceptLabel: "OK",
        onClose: closeNotification,
      });
    }
    setLoading(false);
  };

  // Columns selector based on role 
  const columns = useMemo(() => (role === "Signal" ? SIGNAL_COLUMNS : FOLLOWER_COLUMNS), [role]);

  // Export handlers
  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);
  const handleExport = (type) => {
    if (type === "csv") exportCSV(columns.filter((c) => c.field !== "actions"), accounts, `accounts_${role.toLowerCase()}`);
    if (type === "pdf") exportPDF(columns.filter((c) => c.field !== "actions"), accounts, `accounts_${role.toLowerCase()}`);
    if (type === "txt") exportTXT(columns.filter((c) => c.field !== "actions"), accounts, `accounts_${role.toLowerCase()}`);
    handleExportClose();
  };

  return (
    <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
      <NotificationModal {...modalConfig} />
      <FormSystemModal {...formModalConfig} />
      <Typography variant="h5" gutterBottom>
        Accounts Management
      </Typography>
      <Typography sx={{ fontSize: "15px", color: "#687B8E", mb: 2 }}>
        Take a look at all the Signals and Followers registered.
      </Typography>
      <TabsComponent tabs={TABS} selected={role} onSelect={setRole} />
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 0 }}
        autoComplete="off"
      >
        <SearchBoxComponent
          value={filter}
          handleSearchTerm={(e) => setFilter(e.target.value)}
          placeholderprops={role === "Signal" ? "Signal Id, Name or Strategy" : "Follower Id, Name or Strategy"}
        />
        <SearchBoxComponent
          value={groups}
          handleSearchTerm={(e) => setGroups(e.target.value)}
          placeholderprops="Group"
        />
        <SelectorSimple
          label="Activity (Optional)"
          value={activity}
          onChange={(e) => setActivity(e.target.value)}
          options={activityOptions}
          error={false}
          helperText=""
          name="activity"
          sx={slimSelectorStyles}
        />
        <ButtonMain type="submit">Search</ButtonMain>
        <ExportIconButton onClick={handleExportClick} aria-label="Export table">
          <FileDownloadIcon sx={{ color: "#344555" }} />
        </ExportIconButton>
        <Menu anchorEl={anchorEl} open={exportOpen} onClose={handleExportClose}>
          <MuiMenuItem onClick={() => handleExport("csv")}>Export as CSV</MuiMenuItem>
          <MuiMenuItem onClick={() => handleExport("pdf")}>Export as PDF</MuiMenuItem>
          <MuiMenuItem onClick={() => handleExport("txt")}>Export as TXT</MuiMenuItem>
        </Menu>
      </form>
      {error && (
        <Typography color="error" sx={{ my: 2 }}>
          {error}
        </Typography>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <TableComponent rows={accounts} columns={columns} totalAmount={accounts.length} />
      )}
    </Paper>
  );
};

export default AccountsScreen;